import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { useActiveTab } from 'contexts/ActiveTabContext';
import RoleToggle from './role-toggle/RoleToggle';
import { useTranslation } from 'i18n/TranslationProvider';

const ErrorText = ({ id, children }) => (
  <Text id={id} color="red.500" fontSize={{ base: 'xs', md: 'sm' }} mt={1} role="alert">
    {children}
  </Text>
);

const AuthForms = ({
  name,
  setName,
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  show,
  showConfirmPassword,
  handleClick,
  handleClickConfirmPassword,
  type,
  nameError,
  emailError,
  passwordError,
  confirmPasswordError,
  passwordMatchError,
  invalidEmailError,
}) => {
  const { t } = useTranslation();
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = useColorModeValue('gray.500', 'gray.300');
  const textColorBrand = useColorModeValue('brand.500', 'white');
  const brandStars = useColorModeValue('brand.500', 'brand.300');
  const fieldBg = useColorModeValue('white', 'whiteAlpha.100');
  const fieldBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const { activeTab } = useActiveTab();
  const emailHasError = emailError || invalidEmailError;

  return (
    <Box>
      {type === 'signup' && (
        <Box mb={6}>
          <Flex flexDirection="column" gap={4}>
            {activeTab === 'signup' && (
              <Heading size={{ base: 'md', md: 'lg' }} color={textColor}>
                {t('auth.i_want_to_register_as_a')}
              </Heading>
            )}
            <RoleToggle />
          </Flex>
        </Box>
      )}

      {type === 'signup' && (
        <FormControl isInvalid={nameError} mb={4}>
          <FormLabel
            htmlFor="auth-display-name"
            display="flex"
            ms="2px"
            fontSize="sm"
            fontWeight="600"
            color={textColor}
            mb="6px"
          >
            {t('auth.username')}
            <Text color={brandStars}>*</Text>
          </FormLabel>
          <Input
            isRequired
            variant="auth"
            fontSize={{ base: 'sm', md: 'md' }}
            type="text"
            id="auth-display-name"
            name="displayName"
            autoComplete="name"
            placeholder={t('auth.your_name')}
            fontWeight="500"
            size="lg"
            onChange={(e) => setName(e.target.value)}
            value={name}
            bg={fieldBg}
            borderColor={fieldBorder}
            isInvalid={nameError}
          />
          {nameError && (
            <ErrorText id="auth-display-name-error">
              {t('auth.username_is_required')}
            </ErrorText>
          )}
        </FormControl>
      )}

      <FormControl isInvalid={emailHasError} mb={4}>
        <FormLabel
          htmlFor="auth-email"
          display="flex"
          ms="2px"
          fontSize="sm"
          fontWeight="600"
          color={textColor}
          mb="6px"
        >
          {t('auth.email')}
          <Text color={brandStars}>*</Text>
        </FormLabel>
        <Input
          isRequired
          variant="auth"
          fontSize={{ base: 'sm', md: 'md' }}
          type="email"
          id="auth-email"
          name="email"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          placeholder={t('auth.email_placeholder')}
          fontWeight="500"
          size="lg"
          onChange={(e) => setEmail(e.target.value)}
          value={email}
          bg={fieldBg}
          borderColor={fieldBorder}
          isInvalid={emailHasError}
        />
        {emailError && (
          <ErrorText id="auth-email-required-error">
            {t('auth.email_is_required')}
          </ErrorText>
        )}
        {invalidEmailError && (
          <ErrorText id="auth-email-format-error">
            {t('auth.invalid_email_format')}
          </ErrorText>
        )}
      </FormControl>

      <FormControl isInvalid={passwordError} mb={4}>
        <FormLabel
          htmlFor="auth-password"
          ms="2px"
          fontSize="sm"
          fontWeight="600"
          color={textColor}
          display="flex"
          mb="6px"
        >
          {t('auth.password')}
          <Text color={brandStars}>*</Text>
        </FormLabel>
        <InputGroup size="lg">
          <Input
            isRequired
            fontSize={{ base: 'sm', md: 'md' }}
            placeholder={t('auth.min_8_characters')}
            id="auth-password"
            name="password"
            autoComplete={type === 'signin' ? 'current-password' : 'new-password'}
            size="lg"
            type={show ? 'text' : 'password'}
            variant="auth"
            pr="52px"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            bg={fieldBg}
            borderColor={fieldBorder}
            isInvalid={passwordError}
          />
          <InputRightElement h="100%">
            <IconButton
              type="button"
              aria-label={show ? t('auth.hide_password') : t('auth.show_password')}
              variant="ghost"
              size="md"
              minW="40px"
              h="40px"
              color={textColorSecondary}
              onClick={handleClick}
              icon={<Icon as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye} />}
              _hover={{ bg: 'transparent', color: textColor }}
              _active={{ bg: 'transparent' }}
            />
          </InputRightElement>
        </InputGroup>
        {passwordError && (
          <ErrorText id="auth-password-error">
            {t('auth.password_is_required')}
          </ErrorText>
        )}
      </FormControl>

      {type === 'signup' && (
        <FormControl isInvalid={confirmPasswordError || passwordMatchError} mb={4}>
          <FormLabel
            htmlFor="auth-confirm-password"
            ms="2px"
            fontSize="sm"
            fontWeight="600"
            color={textColor}
            display="flex"
            mb="6px"
          >
            {t('auth.confirm_password')}
            <Text color={brandStars}>*</Text>
          </FormLabel>
          <InputGroup size="lg">
            <Input
              isRequired
              fontSize={{ base: 'sm', md: 'md' }}
              placeholder={t('auth.min_8_characters')}
              id="auth-confirm-password"
              name="confirmPassword"
              autoComplete="new-password"
              size="lg"
              type={showConfirmPassword ? 'text' : 'password'}
              variant="auth"
              pr="52px"
              onChange={(e) => setConfirmPassword(e.target.value)}
              value={confirmPassword}
              bg={fieldBg}
              borderColor={fieldBorder}
              isInvalid={confirmPasswordError || passwordMatchError}
            />
            <InputRightElement h="100%">
              <IconButton
                type="button"
                aria-label={
                  showConfirmPassword
                    ? t('auth.hide_password')
                    : t('auth.show_password')
                }
                variant="ghost"
                size="md"
                minW="40px"
                h="40px"
                color={textColorSecondary}
                onClick={handleClickConfirmPassword}
                icon={
                  <Icon
                    as={
                      showConfirmPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye
                    }
                  />
                }
                _hover={{ bg: 'transparent', color: textColor }}
                _active={{ bg: 'transparent' }}
              />
            </InputRightElement>
          </InputGroup>
          {confirmPasswordError && (
            <ErrorText id="auth-confirm-password-error">
              {t('auth.confirm_password_is_required')}
            </ErrorText>
          )}
          {passwordMatchError && (
            <ErrorText id="auth-password-match-error">
              {t('auth.passwords_do_not_match')}
            </ErrorText>
          )}
        </FormControl>
      )}

      {type === 'signin' && (
        <Flex justifyContent="flex-end" align="center" mb={1}>
          <NavLink to="/auth/forgot-password">
            <Text
              color={textColorBrand}
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight="600"
              _hover={{ textDecoration: 'underline' }}
            >
              {t('auth.forgot_password')}
            </Text>
          </NavLink>
        </Flex>
      )}
    </Box>
  );
};

export default AuthForms;
